import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import FormGroupTile from "../../../components/core/basic/FormGroupTile";
import { useFetch } from "../../../hooks/api";
import { useMemo } from "react";
import { useModuleEnabled } from "../../../redux/selectors/modules";

const BulkAdPlanRulesForm = ({ formData, setFormData, programType }) => {
  const { t } = useTranslation(["advertising"]);
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");

  const { data: biddingProgramData } = useFetch(
    ["programs", "bidding"],
    "/programs",
    { engine: "bidding", all_user_orgs: hasMultiOrgRulesModuleEnabled },
    {
      select: (data) => {
        return data.data?.results;
      },
      enabled: programType === "bidding",
    },
  );

  const { data: budgetProgramData } = useFetch(
    ["programs", "budget"],
    "/programs",
    { engine: "budget", all_user_orgs: hasMultiOrgRulesModuleEnabled },
    {
      select: (data) => {
        return data.data?.results;
      },
      enabled: programType === "budget",
    },
  );

  const bidProgramOptions = useMemo(() => {
    const options =
      biddingProgramData?.map((program) => ({
        value: program.id,
        label:
          hasMultiOrgRulesModuleEnabled ?
            t(($) => $.advertising["{name} (Source: {org})"], { name: program.name, org: program.org.name })
          : program.name,
      })) ?? [];

    return [{ value: null, label: t(($) => $.advertising["No Bidding Program"]) }, ...options];
  }, [biddingProgramData, hasMultiOrgRulesModuleEnabled, t]);

  const budgetProgramOptions = useMemo(() => {
    const options =
      budgetProgramData?.map((program) => ({
        value: program.id,
        label:
          hasMultiOrgRulesModuleEnabled ?
            t(($) => $.advertising["{name} (Source: {org})"], { name: program.name, org: program.org.name })
          : program.name,
      })) ?? [];

    return [{ value: null, label: t(($) => $.advertising["No Budget Program"]) }, ...options];
  }, [budgetProgramData, hasMultiOrgRulesModuleEnabled, t]);

  const handleChange = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const defaultValue = useMemo(() => {
    if (programType === "bidding") {
      return bidProgramOptions?.find((program) => program.value === formData.biddingProgram);
    }

    if (programType === "budget") {
      return budgetProgramOptions?.find((program) => program.value === formData.budgetProgram);
    }
  }, [bidProgramOptions, budgetProgramOptions, formData.biddingProgram, formData.budgetProgram, programType]);

  const programOptions = useMemo(() => {
    if (programType === "bidding") {
      return bidProgramOptions;
    }

    if (programType === "budget") {
      return budgetProgramOptions;
    }
  }, [bidProgramOptions, budgetProgramOptions, programType]);

  const promptText =
    programType === "bidding" ?
      t(($) => $.advertising["Please select bidding rules to apply to the selected media plans."])
    : t(($) => $.advertising["Please select budget rules to apply to the selected media plans."]);

  const tileLabel =
    programType === "bidding" ? t(($) => $.advertising["Bidding Rules"]) : t(($) => $.advertising["Budget Rules"]);

  return (
    <Row className="pb-5">
      <form onSubmit={(e) => e.preventDefault()}>
        <Row className="d-flex justify-content-center">
          <p className="pb-4 fs-4 w-100 text-center">{promptText}</p>
        </Row>
        <Row className="d-flex justify-content-center align-items-center">
          <Col xs={12} md={8}>
            <FormGroupTile
              label={tileLabel}
              type="select"
              multi={false}
              stateName={`${programType}Program`}
              handleChildFormElement={(key, value) => {
                handleChange(key, value.value);
              }}
              defaultValue={defaultValue}
              options={programOptions}
            />
          </Col>
        </Row>
      </form>
    </Row>
  );
};

export default BulkAdPlanRulesForm;
